import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Button, FormControlLabel, Checkbox, IconButton, Tooltip } from '@mui/material';
import { Card, TextField } from '@totalsoft/rocket-ui';
import { Person, LockReset } from '@mui/icons-material';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_USER_GROUPS_WRITE } from 'constants/rightsCodes';
import { emptyString } from 'utils/constants';
import { get, set } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import PersonAutocomplete from './PersonAutocomplete';
import PartnerAutocomplete from './PartnerAutocomplete';

const UserPanel = ({ lens, isDirty, isNew, saving, onSave, onResetPassword, validation }) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_USER_GROUPS_WRITE);
  const disabled = !canWrite || saving;

  const person = useMemo(
    () => ({ id: get(lens.personId) || emptyString, name: get(lens.personName) || emptyString }),
    [lens.personId, lens.personName]
  );
  const partner = useMemo(
    () => ({ id: get(lens.partnerId) || emptyString, name: get(lens.partnerName) || emptyString }),
    [lens.partnerId, lens.partnerName]
  );

  const prevPerson = useRef({ id: get(lens.partnerId), name: get(lens.partnerName) });

  const useNewPerson = get(lens.useNewPerson);
  const handlePersonAdd = useCallback(() => {
    const unp = !useNewPerson;
    if (unp) {
      prevPerson.current = { id: get(lens.partnerId), name: get(lens.partnerName) };
      set(lens.personId)(undefined);
      set(lens.personName)(undefined);
    } else {
      set(lens.personId)(prevPerson.current.id);
      set(lens.personName)(prevPerson.current.name);
      set(lens.personFirstName)(undefined);
      set(lens.personLastName)(undefined);
      set(lens.personUniqueId)(undefined);
    }
    set(lens.useNewPerson)(unp);
  }, [
    lens.partnerId,
    lens.partnerName,
    lens.personFirstName,
    lens.personId,
    lens.personLastName,
    lens.personName,
    lens.personUniqueId,
    lens.useNewPerson,
    useNewPerson
  ]);

  return (
    <Card
      icon={Person}
      iconColor='primary'
      title={t('Users.GeneralData')}
      actions={
        <Tooltip placement='top' arrow title={t('Users.ResetPassword')}>
          <IconButton aria-label='reset' onClick={onResetPassword} color='primary' disabled={disabled || isNew}>
            <LockReset fontSize='inherit' />
          </IconButton>
        </Tooltip>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} xl={4}>
          <TextField
            required
            fullWidth
            value={get(lens.name) || emptyString}
            onChange={event => set(lens.name)(event)}
            label={t('Users.Name')}
            disabled={disabled}
            error={!isValid(validation.name)}
            helperText={getErrors(validation.name)}
          />
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <PartnerAutocomplete
            label={t('Users.Partner')}
            value={partner}
            error={!isValid(validation.partnerId)}
            helperText={getErrors(validation.partnerId)}
            onChange={element => {
              set(lens.partnerId)(element.id);
              set(lens.partnerName)(element.name);
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6} xl={1}>
          <FormControlLabel
            disabled={disabled || isNew}
            control={
              <Checkbox
                checked={get(lens.inactive)}
                onChange={event => set(lens.inactive)(event.target.checked)}
                color='primary'
                size='small'
              />
            }
            label={t('Users.Inactive')}
          />
        </Grid>
        <Grid item xs={6} xl={3}>
          <FormControlLabel
            disabled
            control={
              <Checkbox
                checked={get(lens.passwordChangedByAdmin) || false}
                onChange={event => set(lens.passwordChangedByAdmin)(event.target.checked)}
                color='primary'
                size='small'
              />
            }
            label={t('Users.PasswordResetByAdmin')}
          />
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <PersonAutocomplete
            label={t('Users.Person')}
            value={person}
            error={!isValid(validation.personId)}
            helperText={getErrors(validation.personId)}
            onChange={element => {
              set(lens.personId)(element.id);
              set(lens.personName)(element.name);
            }}
            disabled={disabled || useNewPerson}
            onAdd={handlePersonAdd}
            adding={useNewPerson}
          />
        </Grid>
        {useNewPerson && (
          <>
            <Grid item xs={12} md={6} xl={4}>
              <TextField
                required
                fullWidth
                value={get(lens.personFirstName) || emptyString}
                onChange={event => set(lens.personFirstName)(event)}
                label={t('Users.PersonFirstName')}
                disabled={disabled}
                error={!isValid(validation.personFirstName)}
                helperText={getErrors(validation.personFirstName)}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <TextField
                required
                fullWidth
                value={get(lens.personLastName) || emptyString}
                onChange={event => set(lens.personLastName)(event)}
                label={t('Users.PersonLastName')}
                disabled={disabled}
                error={!isValid(validation.personLastName)}
                helperText={getErrors(validation.personLastName)}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={4}>
              <TextField
                required
                fullWidth
                value={get(lens.personUniqueId) || emptyString}
                onChange={event => set(lens.personUniqueId)(event)}
                label={t('Users.PersonUniqueId')}
                disabled={disabled}
                error={!isValid(validation.personUniqueId)}
                helperText={getErrors(validation.personUniqueId)}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <TextField
            fullWidth
            value={get(lens.remark) || emptyString}
            onChange={event => set(lens.remark)(event)}
            label={t('Users.Remark')}
            disabled={disabled}
            error={!isValid(validation.remark)}
            helperText={getErrors(validation.remark)}
          />
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <TextField fullWidth value={(get(lens.failedLoginAttempts) || 0).toString()} label={t('Users.FailedLoginAttempts')} disabled />
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <TextField fullWidth value={t('LONG_DATE', { date: get(lens.lastLoginDate) })} label={t('Users.LastLoginDate')} disabled />
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <TextField
            fullWidth
            value={t('LONG_DATE', { date: get(lens.passwordChangeDate) })}
            label={t('Users.PasswordChangeDate')}
            disabled
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} pt={2} justifyContent='flex-end'>
        <Button color='primary' variant='contained' disabled={!isDirty || disabled} onClick={onSave}>
          {saving ? t('Saving') : t('Save')}
        </Button>
      </Grid>
    </Card>
  );
};

UserPanel.propTypes = {
  lens: PropTypes.object.isRequired,
  isDirty: PropTypes.bool,
  isNew: PropTypes.bool.isRequired,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onResetPassword: PropTypes.func.isRequired,
  validation: PropTypes.object
};

export default UserPanel;
